import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, User as UserIcon, Info, ShieldCheck, Eye, EyeOff, Loader2 } from "lucide-react";

function PasswordField({ label, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="relative">
        <Input type={show ? "text" : "password"} value={value} onChange={onChange} data-testid={testid} className="pr-10" />
        <button type="button" onClick={() => setShow((v) => !v)} data-testid={`${testid}-toggle`}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 10) { toast.error("Password baru minimal 10 karakter."); return; }
    if (newPw !== confirmPw) { toast.error("Konfirmasi password tidak cocok."); return; }
    if (newPw === oldPw) { toast.error("Password baru tidak boleh sama dengan password lama."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: oldPw, new_password: newPw });
      toast.success("Password berhasil diganti. Silakan login kembali.");
      await logout();
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="settings-page">
      <h1 className="font-heading text-2xl font-bold text-navy">Pengaturan</h1>

      <Card className="p-5">
        <h3 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-2"><UserIcon size={16} /> Profil Akun</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between border-b pb-2"><span className="text-muted-foreground">Nama</span><span className="font-medium">{user?.name}</span></div>
          <div className="flex justify-between border-b pb-2"><span className="text-muted-foreground">Email</span><span className="font-medium">{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Role</span><span className="font-medium text-primary">{user?.role}</span></div>
        </div>
        <Button variant="outline" className="mt-4 text-primary" data-testid="settings-logout" onClick={async () => { await logout(); navigate("/login"); }}>
          <LogOut size={15} className="mr-1.5" /> Keluar
        </Button>
      </Card>

      <Card className="p-5">
        <h3 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> Keamanan Akun — Ganti Password</h3>
        <form onSubmit={changePassword} className="space-y-3">
          <PasswordField label="Password Lama" value={oldPw} onChange={(e) => setOldPw(e.target.value)} testid="old-password" />
          <PasswordField label="Password Baru (min. 10 karakter)" value={newPw} onChange={(e) => setNewPw(e.target.value)} testid="new-password" />
          <PasswordField label="Konfirmasi Password Baru" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} testid="confirm-password" />
          <p className="text-xs text-muted-foreground">Setelah berhasil, Anda akan otomatis keluar dan diminta masuk kembali dengan password baru.</p>
          <Button type="submit" disabled={saving} data-testid="change-password-btn" className="bg-primary hover:bg-red-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldCheck size={15} className="mr-1.5" />} Ganti Password
          </Button>
        </form>
      </Card>

      <Card className="p-5">
        <h3 className="font-heading font-semibold text-navy text-sm mb-2 flex items-center gap-2"><Info size={16} /> Tentang Aplikasi</h3>
        <p className="text-sm text-muted-foreground">K3 REPAIR SYSTEM — Commercial Vehicle Repair & Project Management. Fase 1 (Fondasi).</p>
        <p className="text-xs text-muted-foreground mt-2">Zona waktu: Asia/Jakarta (WIB). Format Project ID: K3-PRJ-TAHUN-NOMOR.</p>
      </Card>
    </div>
  );
}
